import React from "react";
import house1 from "../../assets/house-1.jpg";
import house2 from "../../assets/house-2.jpg";
import house3 from "../../assets/house-3.jpg";
import HomeTop from "./components/home-top";
import Testimonials from "./components/testimonial-section";

// ─── Icons ────────────────────────────────────────────────────────────────────

const ArrowRight = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <path d="M1 7h12m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Plus = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// ─── Constants ────────────────────────────────────────────────────────────────

const FEATURES = [
  "Settlement, title examination, and Deed/Note preparation included",
  "Fees consistently lower than attorney-led settlements",
  "Friendly, knowledgeable staff before and after closing",
  "Latest computerized settlement systems",
  "Direct coordination with your agent and lender",
  "Residential and commercial — MD & DC",
  "Rush settlements available",
  "Free parking at our Greenbelt office",
];

const SERVICES = [
  {
    t: "What is title insurance?",
    d: "Protect your ownership against undiscovered claims, liens, and errors in public record.",
    tag: "Education",
    href: "https://fntic.com/whatistitle.aspx",
    img: house2,
  },
  {
    t: "Title rate estimator",
    d: "Get a transparent quote for settlement, title insurance and recording fees in seconds.",
    tag: "Tool",
    href: "https://ratecalculator.fnf.com/",
    img: house3,
  },
  {
    t: "My Home Approved",
    d: "A streamlined path for buyers — pre-cleared title, ready to close on your timeline.",
    tag: "Program",
    href: "https://www.princegeorgescountymd.gov/2964/First-Time-Home-Buyer-Assistance-Program",
    img: house1,
  },
];

const PROCESS_STEPS = [
  {
    n: "01",
    t: "Get a free quote",
    d: "Send your transaction details — we respond same-day with a transparent fee breakdown.",
  },
  {
    n: "02",
    t: "Title examination",
    d: "We examine the title, prepare the deed, note and deed of trust if required.",
  },
  {
    n: "03",
    t: "Coordinate with your team",
    d: "We work directly with your real estate agent and lender to keep the closing on track.",
  },
  {
    n: "04",
    t: "Settlement at our office",
    d: "Experienced professionals walk you through every document at the table — and free parking.",
  },
];

// ─── Section components ───────────────────────────────────────────────────────

function ValuePropSection() {
  return (
    <>
      {/* Desktop */}
      <section style={{ padding: "120px 64px" }} className="pte-vp-desktop" id="our-services">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 96, alignItems: "start" }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>Why Preferred</div>
            <h2 style={{ fontSize: 56, marginBottom: 24, lineHeight: 1.05 }}>
              You choose your<br />settlement company.<br />
              <span style={{ fontStyle: "italic", color: "var(--maroon-soft)" }}>Choose carefully.</span>
            </h2>
            <p style={{ fontSize: 17, color: "var(--ink-soft)", maxWidth: 460, marginBottom: 32, lineHeight: 1.6 }}>
              When you purchase residential real estate, you have the right to designate your settlement company.
              We make the case for friendlier, more personal service — and competitive fees often lower than attorneys.
            </p>
            <a href="/our-team" style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              color: "var(--maroon)",
              fontWeight: 500,
              fontSize: 15,
              borderBottom: "1px solid var(--maroon)",
              paddingBottom: 4,
              textDecoration: "none",
            }}>
              Meet our team <ArrowRight />
            </a>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
            background: "var(--line)",
            border: "1px solid var(--line)",
            borderRadius: 8,
            overflow: "hidden",
          }}>
            {FEATURES.map((f, i) => (
              <div key={i} style={{
                background: "var(--paper)",
                padding: "28px 24px",
                display: "flex",
                gap: 14,
                alignItems: "flex-start",
              }}>
                <div style={{
                  fontFamily: "var(--mono)",
                  fontSize: 11,
                  color: "var(--gold-deep)",
                  fontWeight: 600,
                  marginTop: 2,
                  width: 24,
                  flexShrink: 0,
                }}>
                  0{i + 1}
                </div>
                <div style={{ fontSize: 15, lineHeight: 1.45, color: "var(--ink)" }}>{f}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile */}
      <section style={{ padding: "64px 20px" }} className="pte-vp-mobile" id="our-services-m">
        <div className="eyebrow" style={{ marginBottom: 12 }}>Why Preferred</div>
        <h2 style={{ fontSize: 36, marginBottom: 16, lineHeight: 1.02 }}>
          You choose your settlement company.{" "}
          <span style={{ fontStyle: "italic", color: "var(--maroon-soft)" }}>Choose carefully.</span>
        </h2>
        <p style={{ fontSize: 15, color: "var(--ink-soft)", lineHeight: 1.55, marginBottom: 24 }}>
          We make the case for friendlier, more personal service — with competitive fees often lower than attorneys.
        </p>
        <div style={{ background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 8 }}>
          {FEATURES.map((f, i) => (
            <div key={i} style={{
              padding: "18px 16px",
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
              borderBottom: i < FEATURES.length - 1 ? "1px solid var(--line)" : "none",
            }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--gold-deep)", fontWeight: 600, marginTop: 3, flexShrink: 0 }}>0{i + 1}</div>
              <div style={{ fontSize: 14, lineHeight: 1.45 }}>{f}</div>
            </div>
          ))}
        </div>
      </section>

      <style>{`
        .pte-vp-desktop { display: block; }
        .pte-vp-mobile  { display: none; }
        @media (max-width: 768px) {
          .pte-vp-desktop { display: none; }
          .pte-vp-mobile  { display: block; }
        }
      `}</style>
    </>
  );
}

function ServicesSection() {
  return (
    <>
      {/* Desktop */}
      <section style={{ padding: "0 64px 120px" }} className="pte-svc-desktop">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 48 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>Title Services</div>
            <h2 style={{ fontSize: 56 }}>Tools &amp; resources<br />for your closing.</h2>
          </div>
          <a
            href="https://fntic.com/whatistitle.aspx"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 14, color: "var(--maroon)", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}
          >
            View all services <ArrowRight />
          </a>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <a
              key={i}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: "var(--paper)",
                border: "1px solid var(--line)",
                borderRadius: 8,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                textDecoration: "none",
                transition: "transform .2s ease, box-shadow .2s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 16px 32px -16px rgba(63,15,18,.2)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
            >
              <div className="house-img" style={{
                height: 220,
                backgroundImage: `url(${s.img})`,
              }}>
                <div style={{
                  position: "absolute", top: 16, left: 16,
                  padding: "4px 10px",
                  background: "rgba(251,247,242,.92)",
                  fontFamily: "var(--mono)",
                  fontSize: 10,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "var(--maroon)",
                  borderRadius: 4,
                  zIndex: 1,
                }}>
                  {s.tag}
                </div>
              </div>
              <div style={{ padding: 28, display: "flex", flexDirection: "column", flex: 1 }}>
                <h3 style={{ fontSize: 26, marginBottom: 12 }}>{s.t}</h3>
                <p style={{ fontSize: 15, color: "var(--ink-soft)", lineHeight: 1.55, flex: 1, marginBottom: 24 }}>{s.d}</p>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--maroon)", fontWeight: 500 }}>
                  Learn more <ArrowRight />
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Mobile */}
      <section style={{ padding: "0 20px 64px" }} className="pte-svc-mobile">
        <div className="eyebrow" style={{ marginBottom: 12 }}>Title Services</div>
        <h2 style={{ fontSize: 36, marginBottom: 24, lineHeight: 1.02 }}>Tools &amp; resources for your closing.</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {SERVICES.map((s, i) => (
            <a
              key={i}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: "var(--paper)",
                border: "1px solid var(--line)",
                borderRadius: 8,
                overflow: "hidden",
                display: "grid",
                gridTemplateColumns: "120px 1fr",
                textDecoration: "none",
              }}
            >
              <div className="house-img" style={{ minHeight: 130, backgroundImage: `url(${s.img})` }} />
              <div style={{ padding: 16 }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--gold-deep)", marginBottom: 6 }}>{s.tag}</div>
                <h3 style={{ fontSize: 18, marginBottom: 6, lineHeight: 1.15 }}>{s.t}</h3>
                <p style={{ fontSize: 12.5, color: "var(--ink-soft)", lineHeight: 1.45, marginBottom: 10 }}>{s.d}</p>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--maroon)", fontWeight: 500 }}>
                  Learn more <ArrowRight size={11} />
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      <style>{`
        .pte-svc-desktop { display: block; }
        .pte-svc-mobile  { display: none; }
        @media (max-width: 768px) {
          .pte-svc-desktop { display: none; }
          .pte-svc-mobile  { display: block; }
        }
      `}</style>
    </>
  );
}

function ProcessSection() {
  return (
    <section style={{
      borderTop: "1px solid var(--line)",
      borderBottom: "1px solid var(--line)",
      background: "var(--cream)",
    }}>
      {/* Desktop */}
      <div style={{ padding: "120px 64px" }} className="pte-proc-desktop">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 96 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>The process</div>
            <h2 style={{ fontSize: 56, marginBottom: 24 }}>From contract to keys —<br />smooth as we can make it.</h2>
            <p style={{ fontSize: 16, color: "var(--ink-soft)", lineHeight: 1.6, maxWidth: 380 }}>
              Most settlements close on time and on budget. Here&rsquo;s what it looks like when you choose us.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {PROCESS_STEPS.map((step, i) => (
              <div key={i} style={{
                display: "grid",
                gridTemplateColumns: "80px 1fr auto",
                gap: 32,
                alignItems: "start",
                padding: "32px 0",
                borderTop: i === 0 ? "none" : "1px solid var(--line)",
              }}>
                <div style={{ fontFamily: "var(--serif)", fontSize: 48, color: "var(--gold-deep)", fontStyle: "italic", lineHeight: 1 }}>
                  {step.n}
                </div>
                <div>
                  <h3 style={{ fontSize: 26, marginBottom: 8 }}>{step.t}</h3>
                  <p style={{ fontSize: 15, color: "var(--ink-soft)", lineHeight: 1.55 }}>{step.d}</p>
                </div>
                <div style={{
                  width: 36, height: 36, borderRadius: "50%",
                  border: "1px solid var(--maroon)",
                  display: "grid", placeItems: "center",
                  color: "var(--maroon)",
                }}>
                  <Plus />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div style={{ padding: "64px 20px" }} className="pte-proc-mobile">
        <div className="eyebrow" style={{ marginBottom: 12 }}>The process</div>
        <h2 style={{ fontSize: 36, marginBottom: 32, lineHeight: 1.02 }}>Contract to keys —<br />smooth as we make it.</h2>
        {PROCESS_STEPS.map((step, i) => (
          <div key={i} style={{
            display: "grid",
            gridTemplateColumns: "52px 1fr",
            gap: 16,
            padding: "24px 0",
            borderTop: i === 0 ? "none" : "1px solid var(--line)",
            alignItems: "start",
          }}>
            <div style={{ fontFamily: "var(--serif)", fontSize: 32, color: "var(--gold-deep)", fontStyle: "italic", lineHeight: 1 }}>{step.n}</div>
            <div>
              <h3 style={{ fontSize: 19, marginBottom: 6 }}>{step.t}</h3>
              <p style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.5 }}>{step.d}</p>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .pte-proc-desktop { display: block; }
        .pte-proc-mobile  { display: none; }
        @media (max-width: 768px) {
          .pte-proc-desktop { display: none; }
          .pte-proc-mobile  { display: block; }
        }
      `}</style>
    </section>
  );
}

function WhySection() {
  return (
    <section style={{ padding: "120px 64px" }}>
      <div style={{ maxWidth: 880, margin: "0 auto", textAlign: "center" }}>
        <div className="eyebrow" style={{ marginBottom: 16 }}>What we believe</div>
        <h2 style={{ fontSize: 64, lineHeight: 1.05, marginBottom: 32 }}>
          Settlement should feel like<br />
          <span style={{ fontStyle: "italic", color: "var(--maroon-soft)" }}>the welcome home</span>, not<br />
          a final hurdle.
        </h2>
        <p style={{ fontSize: 18, color: "var(--ink-soft)", lineHeight: 1.6, maxWidth: 640, margin: "0 auto" }}>
          For 33 years we&rsquo;ve done this one thing — title and escrow — for families and investors in the District
          and Maryland. We answer the phone. We explain the documents. We work weekends when it matters. And our parking is free.
        </p>
      </div>
      <style>{`
        @media (max-width: 768px) {
          section > div[style*="maxWidth: 880"] h2 { font-size: 40px !important; }
          section > div[style*="maxWidth: 880"] { padding: 0 4px; }
        }
      `}</style>
    </section>
  );
}

function CTASection() {
  return (
    <>
      {/* Desktop */}
      <section style={{ padding: "0 64px 120px" }} className="pte-cta-desktop">
        <div className="map-bg" style={{ borderRadius: 12, padding: "80px 64px", color: "var(--cream)", position: "relative", overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 64, alignItems: "center" }}>
            <div>
              <div className="eyebrow" style={{ color: "var(--gold)", marginBottom: 16 }}>Free quote · Same-day reply</div>
              <h2 style={{ color: "var(--cream)", fontSize: 64, lineHeight: 1.02, marginBottom: 24 }}>
                Get your free<br />quote today.
              </h2>
              <p style={{ fontSize: 17, opacity: 0.85, maxWidth: 460, lineHeight: 1.55, color: "var(--cream)" }}>
                Send us a few details about your transaction and we&rsquo;ll respond with a transparent fee estimate the same business day.
              </p>
            </div>
            <div style={{ background: "var(--paper)", color: "var(--ink)", padding: 32, borderRadius: 8 }}>
              <div style={{ display: "grid", gap: 14 }}>
                <StaticField label="Your name" value="Your name here" />
                <StaticField label="Email" value="your@email.com" />
                <StaticField label="Property address" value="Property address" />
                <StaticField label="Transaction type" value="Purchase / Refinance / Other" />
                <a href="https://preferredtitle.titlecapture.com/title-quote" target="_blank" rel="noopener noreferrer" className="btn btn-dark" style={{ width: "100%", padding: 16, marginTop: 8 }}>
                  Send my quote request <ArrowRight />
                </a>
                <div style={{ fontSize: 12, color: "var(--ink-soft)", textAlign: "center", marginTop: 4 }}>
                  Or call <strong style={{ color: "var(--maroon)" }}>(301) 513-9090</strong> · Mon–Fri 9–5
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile */}
      <section style={{ padding: "0 20px 64px" }} className="pte-cta-mobile">
        <div className="map-bg" style={{ borderRadius: 12, padding: 28, color: "var(--cream)" }}>
          <div className="eyebrow" style={{ color: "var(--gold)", marginBottom: 12 }}>Free quote</div>
          <h2 style={{ color: "var(--cream)", fontSize: 36, marginBottom: 12, lineHeight: 1.02 }}>Get your<br />quote today.</h2>
          <p style={{ fontSize: 14, opacity: 0.85, lineHeight: 1.5, marginBottom: 20, color: "var(--cream)" }}>
            Send us a few details and we&rsquo;ll respond with a transparent fee estimate the same business day.
          </p>
          <div style={{ background: "var(--paper)", color: "var(--ink)", padding: 20, borderRadius: 8 }}>
            <div style={{ display: "grid", gap: 12 }}>
              <StaticField label="Email" value="your@email.com" />
              <StaticField label="Property" value="Property address" />
              <StaticField label="Transaction" value="Purchase / Refinance / Other" />
              <a href="https://preferredtitle.titlecapture.com/title-quote" target="_blank" rel="noopener noreferrer" className="btn btn-dark" style={{ width: "100%", padding: 14, marginTop: 4, fontSize: 14 }}>
                Request quote <ArrowRight />
              </a>
            </div>
          </div>
          <div style={{ fontSize: 12, opacity: 0.8, textAlign: "center", marginTop: 16, fontFamily: "var(--mono)", letterSpacing: "0.05em", color: "var(--cream)" }}>
            or call (301) 513-9090
          </div>
        </div>
      </section>

      <style>{`
        .pte-cta-desktop { display: block; }
        .pte-cta-mobile  { display: none; }
        @media (max-width: 768px) {
          .pte-cta-desktop { display: none; }
          .pte-cta-mobile  { display: block; }
        }
      `}</style>
    </>
  );
}

function StaticField({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ borderBottom: "1px solid var(--line)", paddingBottom: 8 }}>
      <div style={{
        fontFamily: "var(--mono)",
        fontSize: 10,
        letterSpacing: "0.15em",
        textTransform: "uppercase",
        color: "var(--ink-soft)",
        marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 15, color: "var(--ink-soft)", fontStyle: "italic" }}>{value}</div>
    </div>
  );
}

function MobileStickyBar() {
  return (
    <div className="pte-sticky-bar">
      <a
        href="tel:3015139090"
        style={{
          flex: 1,
          padding: 12,
          borderRadius: 999,
          background: "transparent",
          color: "var(--cream)",
          fontSize: 13,
          fontWeight: 500,
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          textDecoration: "none",
        }}
      >
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path d="M2 1h2.5l1.5 3.5-2 1c1 2 2 3 4 4l1-2 3.5 1.5v2.5c0 .5-.5 1-1 1C5.5 12.5.5 7.5.5 1.5c0-.5.5-1 1-1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
        </svg>
        Call us
      </a>
      <a
        href="https://preferredtitle.titlecapture.com/title-quote"
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-primary"
        style={{ flex: 1.4, padding: 12, fontSize: 13 }}
      >
        Get a free quote <ArrowRight />
      </a>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Home() {
  return (
    <div>
      <HomeTop />
      <ValuePropSection />
      <ServicesSection />
      <ProcessSection />
      <WhySection />
      <Testimonials />
      <CTASection />
      <MobileStickyBar />
    </div>
  );
}
